/**
 * components/mission/MissionSohPanel.tsx
 * ──────────────────────────────────────
 * Mission-scoped SOH snapshot status + upload (0044).
 *
 * SOH snapshots are uploaded AGAINST A MISSION: the photos and the WMS export
 * are both point-in-time captures of the same inventory, so the report always
 * pairs a mission's photos with the SOH uploaded for that same audit.
 * Workflow: create mission → upload SOH here → assign pilot → upload photos.
 */

import React, { useEffect, useRef, useState } from 'react';
import { apiClient, ApiError } from '@/api/client';

export interface MissionSohStatus {
  snapshot_id: string; filename: string; row_count: number;
  validation_errors_count: number; is_active: boolean;
  uploaded_at: string; uploaded_by_name?: string; uploaded_by_email?: string;
}

interface UploadSummary {
  rows_parsed: number;
  rows_accepted: number;
  rows_rejected: number;
  rows_inserted: number;
  validation_errors?: { row_number: number; field: string; message: string }[];
}

function fmt(iso: string) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

/** Human-friendly error + missing-column hints from an ApiError */
function uploadErrorInfo(err: unknown): { message: string; hints: { col: string; aliases: string[] }[] } {
  if (!(err instanceof ApiError)) return { message: 'Upload failed.', hints: [] };
  const inner = (err.detail as Record<string, unknown> | undefined)?.detail as Record<string, unknown> | undefined;
  const missing: string[] = Array.isArray(inner?.missing_columns) ? inner!.missing_columns as string[] : [];
  const supported: Record<string, string[]> =
    inner?.supported_column_names && typeof inner.supported_column_names === 'object'
      ? inner.supported_column_names as Record<string, string[]>
      : {};
  return { message: err.message, hints: missing.map(col => ({ col, aliases: supported[col] ?? [] })) };
}

export function MissionSohPanel({ missionId, warehouseId, frozen }: {
  missionId: string;
  warehouseId: string;
  /** Frozen missions have their SOH pinned — hide the upload form. */
  frozen?: boolean;
}) {
  const [status, setStatus]   = useState<MissionSohStatus | null | undefined>(undefined);
  const [file, setFile]       = useState<File | null>(null);
  const [busy, setBusy]       = useState(false);
  const [result, setResult]   = useState<UploadSummary | null>(null);
  const [error, setError]     = useState<string | null>(null);
  const [colHints, setColHints] = useState<{ col: string; aliases: string[] }[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  const loadStatus = async () => {
    try {
      const data = await apiClient.get<MissionSohStatus | null>(
        `/api/manage/soh-status/${warehouseId}?mission_id=${missionId}`,
      );
      setStatus(data ?? null);
    } catch { setStatus(null); }
  };

  useEffect(() => { setStatus(undefined); loadStatus(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [missionId, warehouseId]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true); setResult(null); setError(null); setColHints([]);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('mission_id', missionId);
      const data = await apiClient.postForm('/api/manage/upload-soh', fd) as UploadSummary;
      setResult(data);
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      await loadStatus();
    } catch (err) {
      const { message, hints } = uploadErrorInfo(err);
      setError(message);
      setColHints(hints);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* Status card */}
      {status === undefined ? (
        <div className="upload-status-card">Loading mission snapshot…</div>
      ) : status ? (
        <div className="upload-status-card upload-status-card--active">
          <div className="us-header">
            <span className="us-badge us-badge--active">● Mission SOH</span>
            <span className="us-filename" title={status.filename}>{status.filename}</span>
          </div>
          <div className="us-meta-grid">
            <div className="us-meta-item"><span className="us-meta-lbl">Rows loaded</span><strong>{status.row_count.toLocaleString()}</strong></div>
            <div className="us-meta-item"><span className="us-meta-lbl">Rejected</span><strong className={status.validation_errors_count > 0 ? 'us-err' : 'us-ok'}>{status.validation_errors_count.toLocaleString()}</strong></div>
            <div className="us-meta-item"><span className="us-meta-lbl">Uploaded</span><strong>{fmt(status.uploaded_at)}</strong></div>
            <div className="us-meta-item"><span className="us-meta-lbl">By</span><strong>{status.uploaded_by_name ?? status.uploaded_by_email ?? '—'}</strong></div>
          </div>
        </div>
      ) : (
        <div className="upload-status-card upload-status-card--empty">
          <span className="us-icon">📭</span>
          <span>No SOH snapshot uploaded for this mission yet.</span>
        </div>
      )}

      {/* Upload form (hidden when the report is frozen — the SOH is pinned) */}
      {frozen ? (
        <p className="text-sm text-muted-foreground">
          This mission's report is frozen — its SOH reference is pinned and
          cannot be replaced.
        </p>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <div className="form-field">
            <label className="form-label">
              {status ? 'Replace snapshot (.xlsx / .csv)' : 'SOH File (.xlsx / .csv)'}
            </label>
            <input ref={fileRef} className="form-input" type="file"
              accept=".xlsx,.xls,.xlsm,.csv"
              onChange={e => setFile(e.target.files?.[0] ?? null)} required disabled={busy} />
          </div>
          {error && (
            <div className="form-error">
              <div>{error}</div>
              {colHints.length > 0 && (
                <div className="mgr-col-hints">
                  <strong>Rename the missing column to one of these accepted names:</strong>
                  {colHints.map(({ col, aliases }) => (
                    <div key={col} className="mgr-col-hint-row">
                      <span className="mgr-col-hint-missing">{col}</span>
                      <span className="mgr-col-hint-sep">→ accepted:</span>
                      <span className="mgr-col-hint-aliases">{aliases.join(', ')}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {result && (
            <p className="text-sm text-green-700">
              {result.rows_inserted.toLocaleString()} rows loaded
              {result.rows_rejected > 0 && ` · ${result.rows_rejected.toLocaleString()} rejected`}.
            </p>
          )}
          <button className="btn btn--primary" type="submit" disabled={busy || !file}>
            {busy ? 'Uploading…' : status ? 'Replace SOH' : 'Upload SOH'}
          </button>
        </form>
      )}
    </div>
  );
}

export default MissionSohPanel;
